import Vue from 'vue'
import configs from '@/config'
// eslint-disable-next-line import/no-cycle
import store from '@/vuex/store'
import { isStatusPageSourceEnabled, setStatusPageDownHandler } from '@/services/statusPageNotice'
import {
  SYSTEM_STATUS_CHECK_MIN_INTERVAL_MS,
  SYSTEM_STATUS_BAR_FLAG,
  SYSTEM_STATUS_CONFIRM_DELAY_MS,
  SYSTEM_STATUS_HEALTH_PATH,
  SYSTEM_STATUS_RECHECK_INTERVAL_MS,
  SYSTEM_STATUS_REQUEST_TIMEOUT_MS,
  SYSTEM_STATUS_VERDICTS,
} from '@/constant/systemStatus.constant'

export const systemStatusState = Vue.observable({ isActive: false, components: {} })

const HEALTH_DOWN_STATUS = 503
const HEALTH_UNREACHABLE = 'unreachable'

let lastCheckStartedAt = 0
let isChecking = false
let recheckTimer = null
let isAwaitingConfirmation = false

const fetchWithTimeout = async (url) => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), SYSTEM_STATUS_REQUEST_TIMEOUT_MS)
  try {
    return await fetch(url, { cache: 'no-store', signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }
}

const readComponents = async (response) => {
  let timer
  try {
    const body = await Promise.race([
      response.json(),
      new Promise((resolve) => {
        timer = setTimeout(() => resolve(null), SYSTEM_STATUS_REQUEST_TIMEOUT_MS)
      }),
    ])
    return body && typeof body.components === 'object' && body.components !== null ? body.components : {}
  } catch (error) {
    return {}
  } finally {
    clearTimeout(timer)
  }
}

const readHealth = async () => {
  const startedAt = Date.now()
  try {
    const response = await fetchWithTimeout(`${configs.baseUrl}${SYSTEM_STATUS_HEALTH_PATH}`)
    const latencyMs = Date.now() - startedAt
    const { status: httpStatus } = response
    if (httpStatus !== 200 && httpStatus !== HEALTH_DOWN_STATUS) {
      return {
        state: HEALTH_UNREACHABLE, httpStatus, latencyMs, components: {},
      }
    }
    const components = await readComponents(response)
    return {
      state: httpStatus === 200 ? 'ok' : 'down', httpStatus, latencyMs, components,
    }
  } catch (error) {
    return {
      state: HEALTH_UNREACHABLE, httpStatus: null, latencyMs: Date.now() - startedAt, components: {},
    }
  }
}

const canReachCloudfront = async () => {
  try {
    const configurationUrl = configs.region === 'eu'
      ? `${configs.cloudfrontPublic}/configurations/${configs.env === 'production' ? 'config' : 'config-dev'}-eu.json`
      : configs.configFile
    const response = await fetchWithTimeout(`${configurationUrl}?v=${Date.now()}`)
    return response.ok
  } catch (error) {
    return false
  }
}

const isSystemProblem = async () => {
  const health = await readHealth()
  systemStatusState.components = health.components
  if (health.state === 'down') return true
  if (health.state === 'ok') return false
  return navigator.onLine !== false && canReachCloudfront()
}

const hasDownComponent = (components) => Object.values(components || {}).some((value) => value !== 'ok')

const classifySystemStatus = ({ isOnline, health, cloudfrontReachable }) => {
  if (health.state === 'down') return SYSTEM_STATUS_VERDICTS.OUR_SIDE_PROBLEM
  if (health.state === 'ok') {
    return hasDownComponent(health.components) ? SYSTEM_STATUS_VERDICTS.DEGRADED : SYSTEM_STATUS_VERDICTS.OK
  }
  if (isOnline && cloudfrontReachable) return SYSTEM_STATUS_VERDICTS.OUR_SIDE_PROBLEM
  return SYSTEM_STATUS_VERDICTS.CANNOT_REACH_US
}

export const checkSystemStatusFromBrowser = async () => {
  const isOnline = navigator.onLine !== false
  const health = await readHealth()
  const needsFallbackProbe = health.state === HEALTH_UNREACHABLE && isOnline
  const cloudfrontReachable = needsFallbackProbe ? await canReachCloudfront() : null
  return {
    online: isOnline,
    healthReachable: health.state !== HEALTH_UNREACHABLE,
    httpStatus: health.httpStatus,
    latencyMs: health.latencyMs,
    components: health.components,
    cloudfrontReachable,
    verdict: classifySystemStatus({ isOnline, health, cloudfrontReachable }),
  }
}

const setSystemStatusActive = (isActive) => {
  systemStatusState.isActive = isActive
  store.dispatch(`configuration/${isActive ? 'setSystemStatusAnnouncement' : 'clearSystemStatusAnnouncement'}`)
}

const isSystemStatusBarEnabled = () => store.getters['featureToggles/isFeatureEnabled'](SYSTEM_STATUS_BAR_FLAG)

// With the status page as the source, its monitors alone raise and clear the bar.
const isStatusPageSource = () => isStatusPageSourceEnabled(store.getters)

export const setSystemStatusFromStatusPage = (isDown) => {
  if (systemStatusState.isActive !== isDown) setSystemStatusActive(isDown)
}

setStatusPageDownHandler(setSystemStatusFromStatusPage)

const stopIfDisabled = () => {
  if (isSystemStatusBarEnabled()) return false
  isAwaitingConfirmation = false
  if (systemStatusState.isActive) setSystemStatusActive(false)
  return true
}

const scheduleCheck = (delayMs) => {
  if (recheckTimer) return
  recheckTimer = setTimeout(() => {
    recheckTimer = null
    // eslint-disable-next-line no-use-before-define
    runCheck()
  }, delayMs)
}

const runCheck = async () => {
  if (isChecking || isStatusPageSource() || stopIfDisabled()) return
  isChecking = true
  lastCheckStartedAt = Date.now()
  try {
    const hasProblem = await isSystemProblem()
    if (stopIfDisabled()) return
    if (!hasProblem) {
      isAwaitingConfirmation = false
      if (systemStatusState.isActive) setSystemStatusActive(false)
    } else if (systemStatusState.isActive || isAwaitingConfirmation) {
      isAwaitingConfirmation = false
      if (!systemStatusState.isActive) setSystemStatusActive(true)
    } else {
      isAwaitingConfirmation = true
    }
  } finally {
    isChecking = false
  }
  if (systemStatusState.isActive) scheduleCheck(SYSTEM_STATUS_RECHECK_INTERVAL_MS)
  else if (isAwaitingConfirmation) scheduleCheck(SYSTEM_STATUS_CONFIRM_DELAY_MS)
}

export const reportServerFailure = () => {
  if (!isSystemStatusBarEnabled() || isStatusPageSource()) return
  if (isChecking || recheckTimer) return
  if (Date.now() - lastCheckStartedAt < SYSTEM_STATUS_CHECK_MIN_INTERVAL_MS) return
  runCheck()
}

export const reportFailedResponse = (url, status) => {
  if (String(url).includes('/api/') && (status === 0 || status >= 500)) reportServerFailure()
}
